import * as React from 'react';
import * as classnames from 'classnames';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { ContentSpacing } from 'elc-base-theme';
import { ProductDescriptionTheme } from '../../../theme/default-theme';
import { subtitle2Theme } from 'elc-typography';
import { ITranslationsCollection, translate } from 'elc-service';
import {
    PRODUCT_BRIEF_DESCRIPTION,
    PRODUCT_LEARN_MORE_LINK
} from '../../constants/DataTestIdAttributes';

export interface IProductDescription {
    description: string;
    productUrl: string;
    onProductDescriptionClick(): void;
    enableLearnMoreLink?: boolean;
    translations?: ITranslationsCollection;
}

const ProductDescriptionWrapper = styled.a`
    ${subtitle2Theme}
    ${ProductDescriptionTheme}
    margin: ${ContentSpacing.space12} 0;
    cursor: pointer;
`;

const LearnMoreLink = styled.a`
    ${subtitle2Theme}
    ${ProductDescriptionTheme}
    margin-left: ${ContentSpacing.space4};
    text-decoration: underline;

    [dir='rtl'] & {
        margin-left: 0;
        margin-right: ${ContentSpacing.space4};
    }
`;

export const ProductDescription = translate(['learnMoreLabel'])(
    observer((props: IProductDescription) => {
        const ProductDescriptionClassNames = classnames(
            'elc-product-description-wrapper',
            'js-product-description-wrapper'
        );

        const learnMoreLinkClassNames = classnames(
            'elc-product-learn-more-link',
            'js-product-learn-more-link'
        );

        const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
            event.preventDefault();
            props.onProductDescriptionClick();
        };

        const { learnMoreLabel } = props.translations as ITranslationsCollection;

        return (
            <>
                <ProductDescriptionWrapper
                    href={props.productUrl}
                    title={props.description}
                    aria-label={props.description}
                    className={ProductDescriptionClassNames}
                    onClick={handleClick}
                    data-test-id={PRODUCT_BRIEF_DESCRIPTION}
                    dangerouslySetInnerHTML={{ __html: props.description }}
                />
                {props.enableLearnMoreLink && (
                    <LearnMoreLink
                        tabIndex={0}
                        href={props.productUrl}
                        aria-label={learnMoreLabel}
                        title={`${props.description}-${learnMoreLabel}`}
                        className={learnMoreLinkClassNames}
                        onClick={handleClick}
                        data-test-id={PRODUCT_LEARN_MORE_LINK}
                    >
                        {learnMoreLabel}
                    </LearnMoreLink>
                )}
            </>
        );
    })
);
